/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { createContext, useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import jwt from 'jsonwebtoken';
import { useHistory, useLocation } from 'react-router-dom';

import { getUserInfo } from '../services/login';
import useAppProvider from '../hooks/useAppProvider';
import useLocalStorage from '../hooks/useLocalStorage';
import {
  removeLocalAccessToken,
  setLocalAccessToken,
  setLocalRefreshToken
} from '../services/tokenService';
import { injectContext } from '../services/apiInterceptors';
import {
  getYClickRolesAndPermissions,
  getYClickRolesMe,
  MYROLS,
  ROLSANDPERMS
} from '../services/yclickMaster';

const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

const AppProvider = ({ children }) => {
  const [intervalRefresh, setIntervalRefresh] = useState(0);

  const { auth, setAuth, ready, setReady, userInfo, setUserInfo } = useAppProvider();
  const { setLocalStorageItem, removeLocalStorageItem, getLocalStorageItem } = useLocalStorage();

  const { search } = useLocation();
  const history = useHistory();
  const urlSearch = new URLSearchParams(search);

  const queryAccessToken = urlSearch.get('at');
  const queryExpireIn = urlSearch.get('ei');
  const queryRefreshToken = urlSearch.get('rt');
  const queryAccessTokenDate = urlSearch.get('atd');

  // Params relacionados con la la redireccion desde Home
  const accessToken = getLocalStorageItem('access_token') || queryAccessToken;
  const refreshToken = getLocalStorageItem('refresh_token') || queryRefreshToken;
  const accessTokenDate = getLocalStorageItem('access_token_date') || queryAccessTokenDate;
  const accessTokenExpireIn = getLocalStorageItem('access_token_expires_in') || queryExpireIn;

  // Params relacionados al Reporte de Notificación
  const NRisNotificationReport = window.location.pathname.includes('notification-report')
    ? '1'
    : '0';

  const queryNRContractNumber = decodeURIComponent(urlSearch.get('nr_cn'));
  const queryNRWell = decodeURIComponent(urlSearch.get('nr_wl'));
  const queryNRDateFrom = decodeURIComponent(urlSearch.get('nr_df'));
  const queryNRDateTo = decodeURIComponent(urlSearch.get('nr_dt'));

  const handleLogout = () => {
    localStorage.clear();
    removeLocalAccessToken();
    removeLocalStorageItem('userInfo');
    sessionStorage.clear();
    setAuth(false);
    window.localStorage.removeItem('filters');
    window.location.replace('/');
    setUserInfo(null);
    if (intervalRefresh !== null) {
      clearTimeout(intervalRefresh);
    }
  };

  const saveRolsAndPerms = () => {
    Promise.all([getYClickRolesMe(), getYClickRolesAndPermissions()])
      .then(([resRolesMe, resRolesAndPermission]) => {
        sessionStorage.setItem(MYROLS, JSON.stringify(resRolesMe));
        sessionStorage.setItem(ROLSANDPERMS, JSON.stringify(resRolesAndPermission));
        setReady(true);
        setAuth(true);
      })
      .catch((err) => {
        throw new Error(err);
      });
  };

  const saveInfoUser = async (token) => {
    const userInfoStorage = getLocalStorageItem('userInfo');
    const userDefault = {
      uid: '000',
      first_name: 'User',
      last_name: '.',
      tipo_empleado: ''
    };
    let userInfoJson = userInfoStorage ? JSON.parse(userInfoStorage) : userDefault;

    // Abrir el JWT no es la opción definitiva, si no más bien
    // provisoria dado que tenemos problemas con el servicio userInfo
    // TO DO: ver por qué falla el servicio (GF)
    const tokenJWT = jwt.decode(token);

    if (tokenJWT) {
      // Valido con varios If en el caso que en un futuro
      // cambie la estructura de la data del token
      if (!!tokenJWT && !!tokenJWT.token_details) {
        const {
          token_details: {
            Profile: { uid, first_name, last_name, mail }
          }
        } = tokenJWT;
        const firstName = first_name ? first_name.trim() : 'Usuario';
        const lastName = last_name ? last_name.trim() : 'Usuario';

        userInfoJson = {
          ...userInfoJson,
          uid,
          first_name: firstName,
          last_name: lastName,
          mail
        };
        setLocalStorageItem('userInfo', JSON.stringify(userInfoJson));
      }
    } else {
      const result = await getUserInfo(token);

      if (result) {
        const { uid, first_name, last_name, tipo_empleado, mail } = result;

        userInfoJson = {
          uid,
          first_name,
          last_name,
          tipo_empleado,
          mail
        };
        setLocalStorageItem('userInfo', JSON.stringify(userInfoJson));
      }
    }
    const {
      uid = '',
      first_name = 'User',
      last_name = '.',
      tipo_empleado = '',
      mail = ''
    } = userInfoJson || {};

    const infoUser = {
      id: uid,
      name: `${first_name} ${last_name}`,
      type: tipo_empleado,
      mail
    };

    setUserInfo(infoUser);
    saveRolsAndPerms();
  };

  const startIntervalRefreshToken = () => {
    if (intervalRefresh !== 0) {
      clearTimeout(intervalRefresh);
      setIntervalRefresh(0);
    }
    const newInterval = setTimeout(() => {
      const tokenDate = Number(getLocalStorageItem('access_token_date'));
      const tokenExpires = Number(getLocalStorageItem('access_token_expires_in'));
      const tokenExpiresIn = tokenDate + tokenExpires;

      if (Math.floor(new Date().getTime() / 1000) > tokenExpiresIn - 20) {
        clearTimeout(intervalRefresh);
        setIntervalRefresh(0);
      } else {
        startIntervalRefreshToken();
      }
    }, 25000);

    setIntervalRefresh(newInterval);
  };

  const authUser = async (tokenData, date) => {
    setLocalAccessToken(tokenData.access_token, date, tokenData.expires_in);
    setLocalRefreshToken(tokenData.refresh_token);
    startIntervalRefreshToken();
    saveInfoUser(tokenData.access_token);
  };

  const setQueryAccessToken = () => {
    setLocalStorageItem('access_token', queryAccessToken);
    setLocalStorageItem('access_token_date', accessTokenDate);
    setLocalStorageItem('access_token_expires_in', queryExpireIn);
    setLocalStorageItem('refresh_token', queryRefreshToken);

    urlSearch.delete('at');
    urlSearch.delete('ei');
    urlSearch.delete('rt');
    urlSearch.delete('atd');

    history.replace({
      search: urlSearch.toString()
    });
  };

  const setQueryNotificationReportParams = () => {
    if (queryNRContractNumber !== null) {
      setLocalStorageItem('nr_isnr', NRisNotificationReport);
      setLocalStorageItem('nr_cn', queryNRContractNumber);
      setLocalStorageItem('nr_wl', queryNRWell);
      setLocalStorageItem('nr_df', queryNRDateFrom);
      setLocalStorageItem('nr_dt', queryNRDateTo);
    }

    urlSearch.delete('nr_cn');
    urlSearch.delete('nr_wl');
    urlSearch.delete('nr_df');
    urlSearch.delete('nr_dt');

    history.replace({
      search: urlSearch.toString()
    });
  };

  useEffect(() => {
    if (NRisNotificationReport) {
      setQueryNotificationReportParams();
    }
    if (queryAccessToken) {
      setQueryAccessToken();
    }

    if (accessToken) {
      authUser(
        {
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_in: accessTokenExpireIn
        },
        accessTokenDate
      );
    } else if (auth) {
      handleLogout();
    } else {
      setReady(true);
    }

    return () => {
      if (intervalRefresh) clearInterval(intervalRefresh);
    };
  }, []);

  useEffect(() => {
    injectContext({
      handleLogout
    });
  });

  // iFrame remove workaround
  useEffect(() => {
    let interval;

    const removeIframe = () => {
      const iframe = document.querySelector('iframe');

      if (iframe) {
        iframe.remove();
        clearInterval(interval);
      }
    };

    if (process.env.NODE_ENV === 'development') {
      interval = setInterval(() => {
        removeIframe();
      }, 1000);
    }

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <AppContext.Provider
      value={{
        ready,
        setReady,
        auth,
        setAuth,
        userInfo,
        authUser,
        accessToken,
        handleLogout
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

AppProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AppProvider;
